import { alpha, useTheme } from "@mui/material/styles";
import React, { useMemo, useState } from "react";

import { ChartPalette, monospaceStyle, panelShadow } from "../../../theme";
import { SupplyFlow, SupplyFlowBucket } from "../../../types/analytics";
import { LAYOUT, PlacedBand, PlacedNode, SupplyFlowLayout } from "./supplyFlowLayout";
import {
  BUCKET_WORDS,
  MORE_MAKERS_ID,
  bandTitle,
  counted,
  indexNodes,
  nodeLabel,
} from "./supplyFlowModel";

// What the view can have selected: a node or one band, by its position in
// the response
export type FlowSelection =
  | { type: "node"; id: string }
  | { type: "band"; index: number };

// The colours of the flow come from the theme's chart palette: the home
// jurisdiction's teal is `chart.home`, the value chain's; the tint of a band
// into another country on record is `chart.muted`; a need stated with
// nothing of the kind on record is `chart.need`; and the tint of a band under
// the support floor, or into no country, is the overlay `chart.ink`.

export const BAND_OPACITY = {
  stated: 0.28,
  home: 0.35,
  trusted: 0.18,
  other: 0.3,
  faint: 0.08,
} as const;

// A band's fill: a band into a country takes the country's bucket, any
// other the accent of what is on record; under the floor it is faint
export const bandFill = (
  bucket: SupplyFlowBucket | null | undefined,
  thin: boolean,
  accent: string,
  chart: ChartPalette,
): { fill: string; opacity: number } => {
  switch (bucket) {
    case "domestic":
      return { fill: chart.home, opacity: BAND_OPACITY.home };
    case "trusted":
      return { fill: chart.home, opacity: BAND_OPACITY.trusted };
    case "other":
      return { fill: chart.muted, opacity: BAND_OPACITY.other };
    case "unknown":
      return { fill: chart.ink, opacity: BAND_OPACITY.faint };
    default:
      return thin
        ? { fill: chart.ink, opacity: BAND_OPACITY.faint }
        : { fill: accent, opacity: BAND_OPACITY.stated };
  }
};

const dashedBorderOf = (chart: ChartPalette): string => alpha(chart.ink, 0.22);
const hatchOf = (chart: ChartPalette): string =>
  `repeating-linear-gradient(135deg, ${alpha(chart.ink, 0.07)} 0 1.5px, transparent 1.5px 6px)`;
const FLAG_HEIGHT = 24;

const ELLIPSIS: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

interface SupplyFlowChartProps {
  flow: SupplyFlow;
  layout: SupplyFlowLayout;
  selected: FlowSelection | null;
  hoveredId: string | null;
  // Whether the need bands are drawn
  showNeeds?: boolean;
  onSelect: (selection: FlowSelection | null) => void;
  onHover: (id: string | null) => void;
}

// The columns, nodes and ribbons of a layout: the ribbons in SVG, the nodes
// and column heads as boxes over it, so that a name is cut by the box and
// never runs into its neighbour. The layout is never run by a render of the
// chart, only by a new response or a new size.
const SupplyFlowChart: React.FC<SupplyFlowChartProps> = ({
  flow,
  layout,
  selected,
  hoveredId,
  showNeeds = true,
  onSelect,
  onHover,
}) => {
  const theme = useTheme();
  const accent = theme.palette.accent.main;
  const text = theme.palette.text.primary;
  const muted = theme.palette.text.secondary;
  const raised = theme.palette.background.raised;
  const hairline = theme.palette.divider;
  const { chart } = theme.palette;
  const nodes = useMemo(() => indexNodes(flow), [flow]);
  const [hoveredBand, setHoveredBand] = useState<number | null>(null);

  const drawn = useMemo(
    () => layout.bands.filter((placed) => showNeeds || !placed.band.need),
    [layout, showNeeds],
  );

  // The node whose connections are lit: the selected one, else the hovered
  const focusId = selected?.type === "node" ? selected.id : hoveredId;
  const selectedBand = selected?.type === "band" ? selected.index : null;
  const focusBand = selectedBand ?? hoveredBand;
  const lit = (placed: PlacedBand): boolean =>
    focusBand === placed.index ||
    (focusId !== null &&
      (placed.band.source === focusId || placed.band.target === focusId));
  const anyLit = focusId !== null || focusBand !== null;

  // The nodes at either end of a lit band stay bright with it
  const litEnds = new Set<string>();
  if (anyLit) {
    for (const placed of drawn) {
      if (lit(placed)) {
        litEnds.add(placed.band.source);
        litEnds.add(placed.band.target);
      }
    }
  }

  const nodeStyle = (placed: PlacedNode, isSelected: boolean): React.CSSProperties => {
    const { node, height } = placed;
    if (isSelected) {
      return {
        border: `2px solid ${accent}`,
        background: alpha(accent, 0.14),
        color: text,
      };
    }
    // A sliver has no room for a border: it is a dim line
    if (height < 8) {
      return { border: "none", background: alpha(chart.ink, 0.16), color: muted };
    }
    if (node.bucket === "unknown") {
      return {
        border: `1px dashed ${theme.palette.text.disabled}`,
        background: "transparent",
        color: muted,
        fontWeight: 400,
      };
    }
    if (node.id === MORE_MAKERS_ID || node.thin) {
      return {
        border: `1px dashed ${dashedBorderOf(chart)}`,
        background: "transparent",
        color: muted,
        fontWeight: 400,
      };
    }
    if (node.bucket === "domestic") {
      return { border: `2px solid ${chart.home}`, background: raised, color: text };
    }
    if (node.bucket === "trusted") {
      return {
        border: `1px solid ${alpha(chart.home, 0.55)}`,
        background: raised,
        color: text,
      };
    }
    return { border: `1px solid ${hairline}`, background: raised, color: text };
  };

  const select = (selection: FlowSelection) => (event: React.SyntheticEvent) => {
    event.stopPropagation();
    onSelect(selection);
  };
  const onKey = (selection: FlowSelection) => (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(selection);
    }
  };

  // The node named beside its bar, where the bar is too short for a name
  const flagged = layout.nodes.find(
    (placed) =>
      placed.label === "none" &&
      (placed.node.id === hoveredId ||
        (selected?.type === "node" && selected.id === placed.node.id && hoveredId === null)),
  );

  return (
    <div
      role="group"
      aria-label="Supply flow"
      onClick={() => onSelect(null)}
      style={{
        position: "relative",
        width: layout.width,
        height: layout.height,
        fontFamily: theme.typography.fontFamily,
        fontSize: 12,
        lineHeight: 1.3,
      }}
    >
      {/* The heads stay in sight when a long column scrolls */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          height: LAYOUT.headerHeight - 8,
          background: theme.palette.background.paper,
        }}
      >
        {layout.columns.map((column) => (
          <div
            key={column.id}
            style={{
              position: "absolute",
              left: column.x,
              top: 14,
              fontSize: 11,
              fontWeight: 500,
              color: theme.palette.text.disabled,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              whiteSpace: "nowrap",
            }}
          >
            {column.title}
          </div>
        ))}
      </div>

      <svg
        width={layout.width}
        height={layout.height}
        style={{ position: "absolute", inset: 0, display: "block" }}
      >
        <g aria-label="Bands">
          {drawn.map((placed) => {
            const { band } = placed;
            const isLit = lit(placed);
            const dimmed = anyLit && !isLit;
            const title = `${bandTitle(band, nodes)}: ${counted(band.product_count, "product")}${band.need ? " (need)" : ""}`;
            const colours = bandFill(nodes.get(band.target)?.bucket, band.thin, accent, chart);
            // A ribbon thinner than a pixel at an end keeps a hairline
            const hair =
              Math.min(
                placed.sourceBottom - placed.sourceTop,
                placed.targetBottom - placed.targetTop,
              ) < 1.5;
            const selection: FlowSelection = { type: "band", index: placed.index };
            return (
              <path
                key={placed.index}
                data-testid="sf-band"
                data-need={band.need ? "true" : "false"}
                data-thin={band.thin ? "true" : "false"}
                data-active={isLit ? "true" : "false"}
                data-dimmed={dimmed ? "true" : "false"}
                data-source={band.source}
                data-target={band.target}
                role="button"
                tabIndex={0}
                aria-label={title}
                aria-pressed={selectedBand === placed.index}
                d={placed.path}
                fill={band.need ? "none" : colours.fill}
                fillOpacity={
                  band.need ? 0 : isLit ? Math.min(colours.opacity * 2, 0.6) : colours.opacity
                }
                stroke={
                  band.need ? chart.need : isLit || hair ? colours.fill : "none"
                }
                strokeOpacity={
                  band.need ? (isLit ? 1 : 0.8) : isLit ? 0.9 : colours.opacity
                }
                strokeWidth={isLit && band.need ? 1.5 : 1}
                strokeDasharray={band.need ? "6 5" : undefined}
                opacity={dimmed ? 0.25 : 1}
                style={{ cursor: "pointer", outline: "none" }}
                onMouseEnter={() => setHoveredBand(placed.index)}
                onMouseLeave={() => setHoveredBand(null)}
                onFocus={() => setHoveredBand(placed.index)}
                onBlur={() => setHoveredBand(null)}
                onClick={select(selection)}
                onKeyDown={onKey(selection)}
              >
                <title>{title}</title>
              </path>
            );
          })}
        </g>
      </svg>

      <div aria-label="Nodes">
        {layout.nodes.map((placed) => {
          const { node } = placed;
          const isSelected = selected?.type === "node" && selected.id === node.id;
          const label = nodeLabel(node);
          const noun = placed.column === "hosts" ? "host" : "product";
          const count = counted(node.product_count, noun);
          const dimmed = anyLit && !isSelected && node.id !== focusId && !litEnds.has(node.id);
          const selection: FlowSelection = { type: "node", id: node.id };
          const hatched = node.thin && placed.height >= 8 && !isSelected;
          return (
            <div
              key={node.id}
              role="button"
              tabIndex={0}
              aria-label={`${label}, ${count}`}
              aria-pressed={isSelected}
              title={node.bucket ? `${label}: ${BUCKET_WORDS[node.bucket]}` : undefined}
              data-testid="sf-node"
              data-id={node.id}
              data-column={placed.column}
              data-bucket={node.bucket ?? ""}
              data-thin={node.thin ? "true" : "false"}
              data-label={placed.label}
              data-dimmed={dimmed ? "true" : "false"}
              style={{
                position: "absolute",
                left: placed.x,
                top: placed.y,
                width: placed.width,
                height: placed.height,
                boxSizing: "border-box",
                borderRadius: Math.min(theme.radius.surface, placed.height / 2),
                overflow: "hidden",
                cursor: "pointer",
                fontWeight: 500,
                opacity: dimmed ? 0.35 : 1,
                display: "flex",
                flexDirection: placed.label === "full" ? "column" : "row",
                alignItems: placed.label === "full" ? "stretch" : "center",
                justifyContent: placed.label === "full" ? "flex-start" : "space-between",
                gap: placed.label === "full" ? 0 : 8,
                padding: placed.label === "full" ? "5px 10px" : "0 10px",
                ...nodeStyle(placed, isSelected),
              }}
              onMouseEnter={() => onHover(node.id)}
              onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(node.id)}
              onBlur={() => onHover(null)}
              onClick={select(selection)}
              onKeyDown={onKey(selection)}
            >
              {hatched && (
                <span
                  data-testid="sf-hatch"
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: hatchOf(chart),
                    pointerEvents: "none",
                  }}
                />
              )}
              {placed.label === "full" && (
                <>
                  <span style={{ ...ELLIPSIS, position: "relative" }}>{label}</span>
                  <span
                    style={{
                      ...ELLIPSIS,
                      ...monospaceStyle,
                      position: "relative",
                      fontSize: 10.5,
                      fontWeight: 400,
                      color: muted,
                    }}
                  >
                    {count} · {counted(node.articles, "article")}
                  </span>
                </>
              )}
              {placed.label === "line" && (
                <>
                  <span style={{ ...ELLIPSIS, position: "relative", minWidth: 0 }}>
                    {label}
                  </span>
                  <span
                    style={{
                      ...monospaceStyle,
                      position: "relative",
                      flexShrink: 0,
                      fontSize: 10.5,
                      fontWeight: 400,
                      color: muted,
                    }}
                  >
                    {node.product_count}
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>

      {flagged && (
        <div
          data-testid="sf-flag"
          aria-hidden
          style={{
            position: "absolute",
            // Beside the bar, on the side that has room: the last column's
            // flag opens to the left
            ...(flagged.column === "countries"
              ? { right: layout.width - flagged.x + 6 }
              : { left: flagged.x + flagged.width + 6 }),
            top: Math.min(
              Math.max(flagged.y + flagged.height / 2 - FLAG_HEIGHT / 2, LAYOUT.headerHeight - 8),
              layout.height - FLAG_HEIGHT,
            ),
            height: FLAG_HEIGHT,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "0 8px",
            zIndex: 1,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            fontWeight: 500,
            color: text,
            background: raised,
            border: `1px solid ${hairline}`,
            borderRadius: theme.radius.chip,
            boxShadow: panelShadow(theme),
          }}
        >
          {nodeLabel(flagged.node)}
          <span style={{ ...monospaceStyle, fontSize: 10.5, fontWeight: 400, color: muted }}>
            {flagged.node.product_count}
          </span>
        </div>
      )}
    </div>
  );
};

export default SupplyFlowChart;
